import React from "react";
import Plot from "react-plotly.js";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { FileExcel } from 'react-bootstrap-icons';
import ExcelJS from "exceljs";

// Componente para gráficos de superficie 3D - Ideal para datos que tienen 3 dimensiones
const Surface3DCustom = ({ data, xkey, ykey, zkey, title, xlabel, ylabel, zlabel, observaciones }) => {

  // Extraer valores únicos para X e Y
  const uniqueX = [...new Set(data.map(item => item[xkey]))].sort((a, b) => a - b);
  const uniqueY = [...new Set(data.map(item => item[ykey]))].sort((a, b) => a - b);

  // Crear matriz Z para la superficie
  const zMatrix = uniqueY.map(yVal => 
    uniqueX.map(xVal => {
      const point = data.find(item => item[xkey] === xVal && item[ykey] === yVal);
      return point ? point[zkey] : null;
    })
  );

  const plotData = [
    {
      type: 'surface',
      x: uniqueX,
      y: uniqueY,
      z: zMatrix,
      colorscale: 'Viridis',
      showscale: true,
      colorbar: {
        title: zlabel || 'Valores',
        titleside: 'right',
        thickness: 20,
        len: 0.7,
        x: 1.02
      },
      contours: {
        z: {
          show: true,
          usecolormap: true,
          highlightcolor: "#42f462",
          project: { z: true }
        }
      },
      hovertemplate: '<b>Superficie</b><br>' +
                     (xlabel || 'X') + ': %{x}<br>' +
                     (ylabel || 'Y') + ': %{y}<br>' +
                     (zlabel || 'Z') + ': %{z:,.2f}<br>' +
                     '<extra></extra>',
    }
  ];

  const layout = {
    title: {
      text: title || 'Gráfico de Superficie 3D',
      font: { size: 18, color: '#344767' }
    },
    scene: {
      xaxis: { 
        title: xlabel || 'Eje X',
        backgroundcolor: 'rgb(200, 200, 230)',
        gridcolor: 'rgb(255, 255, 255)',
        showbackground: true,
        zerolinecolor: 'rgb(255, 255, 255)',
      },
      yaxis: { 
        title: ylabel || 'Eje Y',
        backgroundcolor: 'rgb(230, 200, 230)',
        gridcolor: 'rgb(255, 255, 255)',
        showbackground: true,
        zerolinecolor: 'rgb(255, 255, 255)',
      },
      zaxis: { 
        title: zlabel || 'Eje Z',
        backgroundcolor: 'rgb(230, 230, 200)',
        gridcolor: 'rgb(255, 255, 255)',
        showbackground: true,
        zerolinecolor: 'rgb(255, 255, 255)',
      },
      camera: {
        eye: { x: 1.87, y: 0.88, z: 0.64 },
        center: { x: 0, y: 0, z: 0 }
      }
    },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    autosize: true,
    margin: { l: 0, r: 0, t: 80, b: 0 },
    height: 600,
  };

  const config = {
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
    toImageButtonOptions: {
      format: 'png',
      filename: title || 'grafico_superficie_3d',
      height: 800,
      width: 1200,
      scale: 2
    }
  };

  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Datos");

    // Encabezados
    const headers = [xlabel || 'X', ylabel || 'Y', zlabel || 'Z'];
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "1976d2" }
      };
      cell.font = { color: { argb: "FFFFFF" }, bold: true };
    });

    // Datos
    data.forEach((item) => {
      worksheet.addRow([
        item[xkey],
        item[ykey],
        item[zkey]
      ]);
    });

    // Ajustar ancho de columnas
    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength < 10 ? 10 : maxLength + 2;
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${title || 'grafico'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    });
  };

  if (!data || data.length === 0) {
    return (
      <MDBox p={3}>
        <MDTypography variant="h5" fontWeight="medium" mb={2}>
          {title}
        </MDTypography>
        <MDTypography variant="body2" color="text">
          No hay datos disponibles
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox p={3}>
      <MDBox style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <MDTypography variant="h5" fontWeight="medium">
          {title}
        </MDTypography>
        <MDBox style={{ display: 'flex', gap: '8px' }}>
          <MDButton
            variant="outlined"
            color="success"
            size="small"
            startIcon={<FileExcel />}
            onClick={exportToExcel}
          >
            Excel
          </MDButton>
        </MDBox>
      </MDBox>

      <MDBox 
        sx={{ 
          backgroundColor: 'white', 
          borderRadius: '8px',
          padding: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Plot
          data={plotData}
          layout={layout}
          config={config}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler={true}
        />
      </MDBox>

      {observaciones && (
        <MDTypography variant="caption" color="text" mt={2} style={{ fontStyle: 'italic', opacity: 0.7 }}>
          *{observaciones}
        </MDTypography>
      )}
    </MDBox>
  );
};

export default Surface3DCustom;
